/**
 * Per-hostApp default suggestion chips for the morph palette's empty
 * body. Hosts can override via the `agentSuggestions` prop on each
 * surface; the rich-shape `starterPromptsFor()` powers the morph
 * palette's SUGGESTED chip row.
 */

interface ModeSuggestions {
  ask: string[]
  agent: string[]
}

export const DEFAULT_SUGGESTIONS: Record<string, ModeSuggestions> = {
  tasker: {
    ask: ["What's blocking my project?", 'Triage this week’s bug reports', 'Which of my tasks slipped?'],
    agent: ['File a bug from this chat', 'Create a personal task for tomorrow', 'Reassign overdue tasks'],
  },
  boombox: {
    ask: ['Which templates went out this week?', 'Show me drafts I haven’t sent', 'Find contacts missing emails'],
    agent: ['Draft a follow-up to my last contact', 'Schedule a reminder for tomorrow'],
  },
  hq: {
    ask: ['What did the team ship this week?', 'Who joined recently?', 'Who is on leave?'],
    agent: ['Onboard a new team member', "Update someone's permissions"],
  },
  customers: {
    ask: ['Brief me on this customer', 'When did we last talk to them?', 'What issues are open?'],
    agent: ['Add a note to this customer', 'Schedule a follow-up'],
  },
  rfp: {
    ask: ['Which RFPs are stuck?', 'Summarize this proposal', 'Show me deals closing this month'],
    agent: ['Move this RFP forward a stage', 'Assign this deal to someone'],
  },
  road_warrior: {
    ask: ["What's on my schedule today?", 'When am I free this week?', 'Where am I going next?'],
    agent: ['Schedule a school visit', 'Move my next appointment'],
  },
  factory: {
    ask: ['Show recent factory runs', 'Which deploys failed?'],
    agent: ['Re-run the last build'],
  },
  agent: {
    ask: ['What can you do?', 'Summarize my recent activity'],
    agent: ['Help me get started'],
  },
}

const GENERIC: ModeSuggestions = {
  ask: ['What can you do?', "Summarize what's happening here"],
  agent: ['Help me get started', 'What can you do?'],
}

export function suggestionsFor(hostApp: string, mode: 'ask' | 'agent'): string[] {
  const entry = DEFAULT_SUGGESTIONS[hostApp] ?? GENERIC
  return entry[mode]
}

/** Rich-shape starters for the morph palette's SUGGESTED chips.
 *  Pulls from the same per-host list as `suggestionsFor` (agent mode);
 *  caps at 4 to match the reference design's chip row. */
export interface StarterPrompt {
  id: string
  label: string
  prompt: string
}

export function starterPromptsFor(hostApp: string, max = 4): StarterPrompt[] {
  const entry = DEFAULT_SUGGESTIONS[hostApp] ?? GENERIC
  // Mix ask + agent for variety; agent comes first since the embed is
  // single-mode and biases toward action.
  const mixed = [...entry.agent, ...entry.ask]
  return mixed.slice(0, max).map((label, idx) => ({
    id: `${hostApp}-${idx}`,
    label,
    prompt: label,
  }))
}
